import { HttpClient, robotoHeaders } from "@/shared/http";

import {
  ClaimSlackLinkRequest,
  CompleteSlackOAuthRequest,
  SetSlackOutboundChannelsRequest,
  SlackChannelSummary,
  SlackConnectionRecord,
  SlackLinkNonceDisplayRecord,
  SlackOutboundChannelRecord,
  SlackUserLinkRecord,
  StartSlackOAuthResponse,
} from "./SlackRecord";

interface Options {
  abortSignal?: AbortSignal;
}

export interface SlackWorkspaceRef {
  enterpriseId: string;
  teamId: string;
}

/** Replaces the workspace's allowlist with exactly these channel ids. */
export interface SetSlackOutboundChannelsInput {
  workspace: SlackWorkspaceRef;
  channelIds: string[];
}

/**
 * The org-level Slack integration: OAuth connect, channel picking, disconnect.
 *
 * Org-scoped by the `X-Roboto-Org-Id` header. Any org member may read the connection list and
 * the outbound allowlist; every other route answers 401 to a caller who is not an org admin.
 */
export class SlackService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async startOAuthFlow(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<StartSlackOAuthResponse> {
    const requestUrl = this.#httpClient.constructUrl("v1/slack/oauth/start");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<StartSlackOAuthResponse>();
  }

  public async completeOAuthFlow(
    request: CompleteSlackOAuthRequest,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SlackConnectionRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/slack/oauth/complete");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
      // The route spends the single-use authorization code against slack.com; a replay after
      // a timeout or dropped connection answers `invalid_code` and buries the real failure.
      idempotent: false,
    });
    return response.json<SlackConnectionRecord>();
  }

  public async listConnections(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SlackConnectionRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/slack/connections");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<SlackConnectionRecord[]>();
  }

  public async disconnectWorkspace(
    workspace: SlackWorkspaceRef,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/slack/connections/${encodeURIComponent(workspace.teamId)}`,
      new URLSearchParams({ enterprise_id: workspace.enterpriseId }),
    );
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      // The route answers 404 once the link is gone, so a disconnect that succeeded but lost
      // its response would replay and report "no such link" for its own success.
      idempotent: false,
    });

    // 204 with no body, so there is no `json()` call to raise: without this a non-admin's 401
    // would resolve as success.
    return response.throwIfError();
  }

  public async listWorkspaceChannels(
    workspace: SlackWorkspaceRef,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SlackChannelSummary[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/slack/connections/${encodeURIComponent(workspace.teamId)}/channels`,
      new URLSearchParams({ enterprise_id: workspace.enterpriseId }),
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<SlackChannelSummary[]>();
  }

  public async listOutboundChannels(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SlackOutboundChannelRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/slack/outbound-channels",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<SlackOutboundChannelRecord[]>();
  }

  public async setOutboundChannels(
    request: SetSlackOutboundChannelsInput,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<SlackOutboundChannelRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/slack/outbound-channels",
    );
    const body: SetSlackOutboundChannelsRequest = {
      enterprise_id: request.workspace.enterpriseId,
      team_id: request.workspace.teamId,
      channels: request.channelIds,
    };
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(body),
    });
    return response.json<SlackOutboundChannelRecord[]>();
  }

  /**
   * What the confirmation page shows: which Slack account this link would bind.
   *
   * No org header — the person following a link may not yet share an org with anyone, and has to
   * see the account before deciding. Reading does not spend the link; an unknown, expired, or
   * already-spent nonce is one indistinguishable 404.
   */
  public async getLinkNonce(
    nonce: string,
    options?: Partial<Options>,
  ): Promise<SlackLinkNonceDisplayRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/slack/links/nonce/${encodeURIComponent(nonce)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<SlackLinkNonceDisplayRecord>();
  }

  /**
   * Spend the link, binding its Slack account to the signed-in Roboto user.
   *
   * A POST from a page that names the account, rather than something a bare link can trigger:
   * the danger is being walked into spending a stranger's nonce, which lets them speak as you.
   */
  public async claimLink(
    nonce: string,
    options?: Partial<Options>,
  ): Promise<SlackUserLinkRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/slack/links");
    const body: ClaimSlackLinkRequest = { nonce };
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(body),
      // The nonce is single-use, so a replay after a dropped connection answers "invalid or
      // expired" and buries whatever really went wrong.
      idempotent: false,
    });
    return response.json<SlackUserLinkRecord>();
  }
}
